// Import authenticated API utility
import { api } from './api.util';
// Import centralized API configuration
import { API_URL } from '../config/api.config';

const API_BASE_URL = API_URL;

// Import exam utilities (exams are now the top-level entities)
export { fetchExams as fetchSubjects, getExamBySlug as getSubjectBySlug } from './exams.util';

// Fetch videos by subject ID
export const fetchVideosBySubject = async (subjectId) => {
  try {
    const data = await api.get(`/subjects/${subjectId}/videos`);
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch videos');
    }
  } catch (error) {
    console.error('Error fetching videos by subject:', error);
    
    // Fallback data for demo purposes when API is not available
    const fallbackVideos = getFallbackVideosBySubject(subjectId);
    if (fallbackVideos.length > 0) {
      console.warn('Using fallback video data due to API connectivity issues');
      return fallbackVideos;
    }
    
    throw error;
  }
};

// Fetch mock tests by subject ID
export const fetchMockTestsBySubject = async (subjectId) => {
  try {
    const data = await api.get(`/subjects/${subjectId}/mock-tests`);
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch mock tests');
    }
  } catch (error) {
    console.error('Error fetching mock tests by subject:', error);
    
    // Fallback data for demo purposes when API is not available
    const fallbackMockTests = getFallbackMockTestsBySubject(subjectId);
    if (fallbackMockTests.length > 0) {
      console.warn('Using fallback mock test data due to API connectivity issues');
      return fallbackMockTests;
    }
    
    throw error;
  }
};

// Fetch materials by subject ID
export const fetchMaterialsBySubject = async (subjectId) => {
  try {
    const data = await api.get(`/subjects/${subjectId}/materials`);
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch materials');
    }
  } catch (error) {
    console.error('Error fetching materials by subject:', error);
    
    // Fallback data for demo purposes when API is not available
    const fallbackMaterials = getFallbackMaterialsBySubject(subjectId);
    if (fallbackMaterials.length > 0) {
      console.warn('Using fallback materials data due to API connectivity issues');
      return fallbackMaterials;
    }
    
    throw error;
  }
};

// Get secure video stream URL
export const getVideoStreamUrl = async (videoId) => {
  try {
    const response = await api.get(`/videos/${videoId}/stream`);
    return response;
  } catch (error) {
    console.error('Error fetching video stream URL:', error);
    throw error;
  }
};

// Helper function to get fallback videos by subject
const getFallbackVideosBySubject = (subjectId) => {
  const fallbackData = {
    'rrb': [
      {
        _id: 'video-1',
        title: 'Introduction to RRB Examination',
        description: 'Complete overview of RRB examination pattern',
        createdAt: new Date().toISOString(),
        durationSec: 1800,
        order: 1
      },
      {
        _id: 'video-2',
        title: 'General Awareness for RRB',
        description: 'Current affairs and general knowledge topics',
        createdAt: new Date().toISOString(),
        durationSec: 2400,
        order: 2
      }
    ],
    'di': [
      {
        _id: 'video-3',
        title: 'Data Interpretation Basics',
        description: 'Introduction to data interpretation concepts',
        createdAt: new Date().toISOString(),
        durationSec: 2100,
        order: 1
      }
    ],
    'pharmacology': [
      {
        _id: 'video-4',
        title: 'Pharmacology Fundamentals',
        description: 'Basic principles of pharmacology',
        createdAt: new Date().toISOString(),
        durationSec: 2700,
        order: 1
      }
    ]
  };

  return fallbackData[subjectId?.toLowerCase()] || [];
};

// Helper function to get fallback mock tests by subject
const getFallbackMockTestsBySubject = (subjectId) => {
  const fallbackData = {
    'rrb': [
      {
        _id: 'mock-test-1',
        title: 'RRB Mock Test 1 - General Knowledge',
        fileUrl: 'https://example.com/rrb-mock-test-1.pdf',
        totalMarks: 100,
        timeLimitMin: 120,
        order: 1,
        createdAt: new Date().toISOString(),
        subject: { _id: subjectId, title: 'RRB' }
      },
      {
        _id: 'mock-test-2',
        title: 'RRB Mock Test 2 - Reasoning',
        fileUrl: 'https://example.com/rrb-mock-test-2.pdf',
        totalMarks: 100,
        timeLimitMin: 120,
        order: 2,
        createdAt: new Date().toISOString(),
        subject: { _id: subjectId, title: 'RRB' }
      }
    ],
    'di': [
      {
        _id: 'mock-test-3',
        title: 'Data Interpretation Mock Test 1',
        fileUrl: 'https://example.com/di-mock-test-1.pdf',
        totalMarks: 50,
        timeLimitMin: 60,
        order: 1,
        createdAt: new Date().toISOString(),
        subject: { _id: subjectId, title: 'Data Interpretation' }
      }
    ],
    'pharmacology': [
      {
        _id: 'mock-test-4',
        title: 'Pharmacology Mock Test 1',
        fileUrl: 'https://example.com/pharm-mock-test-1.pdf',
        totalMarks: 75,
        timeLimitMin: 90,
        order: 1,
        createdAt: new Date().toISOString(),
        subject: { _id: subjectId, title: 'Pharmacology' }
      }
    ]
  };

  return fallbackData[subjectId?.toLowerCase()] || [];
};

// Helper function to get fallback materials by subject
const getFallbackMaterialsBySubject = (subjectId) => {
  const fallbackData = {
    'rrb': [
      {
        _id: 'material-1',
        title: 'RRB Study Guide',
        fileUrl: 'https://example.com/rrb-study-guide.pdf',
        createdAt: new Date().toISOString(),
        subject: { _id: subjectId, title: 'RRB' }
      },
      {
        _id: 'material-2',
        title: 'RRB Previous Year Papers',
        fileUrl: 'https://example.com/rrb-previous-year-papers.pdf',
        createdAt: new Date().toISOString(),
        subject: { _id: subjectId, title: 'RRB' }
      }
    ],
    'di': [
      {
        _id: 'material-3',
        title: 'Data Interpretation Practice Book',
        fileUrl: 'https://example.com/di-practice-book.pdf',
        createdAt: new Date().toISOString(),
        subject: { _id: subjectId, title: 'Data Interpretation' }
      }
    ],
    'pharmacology': [
      {
        _id: 'material-4',
        title: 'Pharmacology Notes',
        fileUrl: 'https://example.com/pharm-notes.pdf',
        createdAt: new Date().toISOString(),
        subject: { _id: subjectId, title: 'Pharmacology' }
      }
    ]
  };

  return fallbackData[subjectId?.toLowerCase()] || [];
};

// Fetch subjects for an exam (backward compatibility)
export const fetchSubjectsByExam = async (examSlug) => {
  try {
    // This function now fetches subjects under an exam
    const { fetchSubjectsByExam } = await import('./exams.util');
    return await fetchSubjectsByExam(examSlug);
  } catch (error) {
    console.error('Error fetching subjects by exam:', error);
    throw error;
  }
}; 